import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBook,
  faLaptop,
  faGraduationCap,
  faBrain,
  faShieldAlt,
  faPaintBrush,
  faUsers,
  faLightbulb,
  faCogs,
  faGlobeAsia,
  faLanguage,
  faChartLine,
  faPlus,
  faEye,
  faTimes,
  faEdit,
  faTrash,
  faLock,
  faUnlock,
} from "@fortawesome/free-solid-svg-icons";
import { motion, AnimatePresence } from "framer-motion";
import { hasPermission } from "../../../utils/permissionUtils";
import {
  showErrorToast,
  showSuccessToast,
  showWarningToast,
  showConfirmDialog,
} from "../../../components/ConfirmDialog";
import { formatDateVN } from "../../../utils/formatUtils";
import { confirmDelete, DELETE_ENTITY } from "../../../utils/confirmDelete";


const ICON_MAP = {
  book: faBook,
  laptop: faLaptop,
  graduation: faGraduationCap,
  brain: faBrain,
  shield: faShieldAlt,
  paint: faPaintBrush,
  users: faUsers,
  idea: faLightbulb,
  tech: faCogs,
  global: faGlobeAsia,
  language: faLanguage,
  analysis: faChartLine,
};


const SubjectDetailModal = ({
  isOpen,
  subject,
  onClose,
  onEdit,
  onDelete,
  onToggleStatus,
}) => {
  if (!subject) return null;

  const handleDelete = async () => {
    const result = await confirmDelete({
      type: DELETE_ENTITY.subject,
      name: subject.name,
    });

    if (result.isConfirmed) {
      onDelete(subject.id);
    }
  };

  const handleToggle = async () => {

    if (subject.status === "draft") {
      showWarningToast("Môn học đang ở trạng thái 'Sắp triển khai', không thể thay đổi trạng thái.");
      return;
    }
  
    const newStatus =
      subject.status === "active"
        ? "inactive"
        : "active";
  
    const title =
      newStatus === "active"
        ? "Kích hoạt môn học"
        : "Ngưng sử dụng môn học";
  
    const message =
      newStatus === "active"
        ? `Bạn có muốn kích hoạt môn "${subject.name}"?`
        : `Bạn có muốn ngưng sử dụng môn "${subject.name}"?`;
  
    const result = await showConfirmDialog({
      title,
      text: message,
      confirmText: "Xác nhận",
      cancelText: "Hủy",
    });
    
    if (result.isConfirmed) {
      onToggleStatus(subject.id, newStatus);
    }
  };


  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[999]
                     bg-black/40 backdrop-blur-sm
                     flex items-center justify-center"
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="bg-white rounded-2xl shadow-2xl
                       w-[550px] max-w-[95%] p-6"
          >
            {/* ================= HEADER ================= */}
            <div className="flex items-center justify-between mb-4 border-b pb-3">
              <h2 className="text-lg font-semibold text-gray-800">
                Chi tiết môn học
              </h2>
              <button onClick={onClose}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            {/* ================= INFO ================= */}
            <div className="flex items-start gap-4 mb-4">

              {/* ICON */}
              <div
                className="w-16 h-16 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: subject.color || "#E5E7EB" }}
              >
                <FontAwesomeIcon
                  // icon={["fas", subject.icon || "book"]}
                  icon={ICON_MAP[subject.icon] || faBook}
                  className="text-white text-2xl"
                />
              </div>

              {/* TEXT */}
              <div className="flex-1 space-y-1">
                <div className="text-xl font-semibold text-gray-900">
                  {subject.name}
                </div>
                <div className="text-sm text-gray-500">
                  Mã môn: <span className="font-medium">{subject.code}</span>
                </div>

                <div
                  className={`inline-block text-xs px-2 py-1 rounded-full font-semibold
                    ${subject.status === "active"
                      ? "bg-green-100 text-green-700"
                      : "bg-gray-200 text-gray-600"}
                  `}
                >
                  {subject.status === "active"
                    ? "Hoạt động"
                    : subject.status === "draft"
                    ? "Sắp triển khai"
                    : "Ngưng hoạt động"}
                </div>
              </div>
            </div>

            {/* DESCRIPTION */}
            {subject.description && (
              <div className="mb-4">
                <label className="text-sm text-gray-600">Mô tả</label>
                <div className="mt-1 text-gray-700 text-sm whitespace-pre-line">
                  {subject.description}
                </div>
              </div>
            )}

            {/* META */}
            <div className="grid grid-cols-[120px_1fr] gap-y-2 text-sm text-gray-600 mb-4">

              <div className="font-medium">Thứ tự:</div>
              <div>{subject.sort_order ?? 0}</div>

              <div className="font-medium">Khóa học:</div>
              <div>{subject.course_count ?? 0}</div>
            </div>

            {/* FOOT INFO */}
            <div className="pt-3 border-t text-xs text-gray-400 flex justify-between">
              <span>Tạo bởi: {subject.created_by_name}</span>

              <span>
                Cập nhật:{" "}
                {subject.updated_at
                  ? formatDateVN(subject.updated_at)
                  : "—"}
              </span>
            </div>

            {/* ================= ACTIONS ================= */}
            <div className="flex justify-end gap-2 pt-4 border-t mt-4">

              {hasPermission("elearning.subjects", "U") && (
                <button
                  onClick={() => onEdit(subject)}
                  className="px-3 py-2 rounded border
                             flex items-center gap-2
                             hover:bg-gray-50"
                >
                  <FontAwesomeIcon icon={faEdit} />
                  Sửa
                </button>
              )}

              {hasPermission("elearning.subjects", "U") && (
                <button
                  onClick={handleToggle}
                  className={`
                    px-3 py-2 rounded border
                    flex items-center gap-2
                    ${subject.status === "active"
                      ? "text-orange-600 hover:bg-orange-50"
                      : "text-green-600 hover:bg-green-50"}
                  `}
                >
                  <FontAwesomeIcon
                    icon={subject.status === "active" ? faLock : faUnlock}
                  />

                  {subject.status === "active" ? "Khóa" : "Mở"}
                </button>
              )}

              {hasPermission("elearning.subjects", "D") && Number(subject.course_count) === 0 && (
                <button
                  onClick={handleDelete}
                  className="px-3 py-2 rounded border
                             text-red-600 hover:bg-red-50
                             flex items-center gap-2"
                >
                  <FontAwesomeIcon icon={faTrash} />
                  Xóa
                </button>
              )}
            </div>

          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SubjectDetailModal;
